/**
 * English UI catalogue for namespace 'publish' (W2-09 publish-import): the Publish workspace (check summary,
 * compatibility, stats, package form, export section), the Export dialog, the `file.export` / `publish.*` commands,
 * their settings rows and help topic.
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('publish', {
  // ---- panels ----
  panelOverview: 'Check',
  panelPackage: 'Package and export',
  noProjectTitle: 'No chart open',
  noProjectBody: 'Open a chart to check it and export it for Phira.',

  // ---- check summary ----
  checkTitle: 'Check',
  checkAgain: 'Check again',
  sevFatal:
    '{count, plural, =0 {Nothing stops Phira from loading the chart} one {# problem stops Phira from loading the chart} other {# problems stop Phira from loading the chart}}',
  sevError: '{count, plural, =0 {No errors} one {# error} other {# errors}}',
  sevWarning: '{count, plural, =0 {No warnings} one {# warning} other {# warnings}}',
  sevTip: '{count, plural, =0 {No tips} one {# tip} other {# tips}}',
  view: 'View',
  viewIn:
    'Show {severity, select, fatal {the blocking problems} error {the errors} warning {the warnings} other {the tips}} in the Problems panel',
  viewBlocking: 'Show the problems that block the export',
  severityWord: '{severity, select, fatal {Blocks export} error {Error} warning {Warning} other {Tip}}',
  blockingTitle: 'Fix before exporting',
  topTitle: 'Most important',
  moreInProblems: '{count, plural, one {# more} other {# more}} in the Problems panel',
  go: 'Go',
  goTo: 'Go to the problem: {message}',

  // ---- compatibility ----
  compatTitle: 'Compatibility',
  phiraName: 'Phira',
  rpeName: 'RPE 1.6 and later',
  phiraOk: 'Renders as previewed',
  phiraOkNotes: 'Renders as previewed. Notes:',
  phiraOkExceptEffects:
    '{count, plural, one {Renders as previewed except # built-in effect (approximate)} other {Renders as previewed except # built-in effects (approximate)}}. Notes:',
  noteApproxEffects:
    'Phira draws {count, plural, one {the built-in effect} other {the built-in effects}} {names} with its own shaders; the preview shows an approximation.',
  phiraBlockedStatus: 'Cannot load the chart yet',
  phiraDiffers: 'Plays differently from the preview',
  rpeOk: 'Opens the package',
  rpeOkNotes: 'Opens the package. {count, plural, one {# note} other {# notes}}:',
  unverified: 'Unverified',
  unverifiedTip:
    'The RPE-compatible package has not been checked on a real RPE install yet. It follows the RPE format exactly, but RPE itself may still show details differently.',
  phiraBlocked:
    '{count, plural, one {# problem stops} other {# problems stop}} Phira from loading the chart (see Check).',
  noteBezierCollisions:
    '{curves, plural, one {# Bezier curve shares} other {# Bezier curves share}} the rounded cache key of another curve, so Phira uses the first curve for {events, plural, one {# event} other {# events}}. The preview already shows it this way.',
  rpeNoInfoTxt:
    'No info.txt is written (Minimal profile, or turned off in Advanced), so RPE cannot import the package by itself.',
  rpeEasings:
    '{count, plural, one {# segment uses} other {# segments use}} "Overshoot, slow both" (23) or "Springy, slow both" (29), which RPE draws differently from Phira.',
  rpeBezierHandles:
    '{count, plural, one {# Bezier curve has} other {# Bezier curves have}} a handle outside 0 to 1 (imported). Phira plays it; the curve editor of RPE may not handle it.',
  rpeNoteFields:
    '{count, plural, one {# note uses} other {# notes use}} a note colour or judge width, which need RPE 1.7. Older RPE versions drop them.',
  rpeSpeedCurves: 'RPE 1.7 speed curves are on: eased scroll speed looks the same only in RPE 1.7 or later.',
  rpeRotateWithParent:
    '{count, plural, one {# child line turns} other {# child lines turn}} with the parent line, which needs RPE 1.6.3 or later.',
  rpeLayers: '{count, plural, one {# line uses} other {# lines use}} more than 4 layers; RPE shows only the first 4.',
  rpeExtraJson:
    'Shader effects and videos ({effects, plural, one {# effect} other {# effects}}, {videos, plural, one {# video} other {# videos}}) are Phira-only; RPE ignores extra.json.',
  rpeMusicCodec: 'RPE cannot open {codec} music. Convert the song to WAV, OGG or MP3 for RPE.',
  rpeImageCodec: 'RPE may not show a {codec} illustration. PNG or JPG is the safest choice.',

  // ---- stats ----
  statsTitle: 'Stats',
  statsNotes: '{notes, plural, one {# note} other {# notes}} ({real} real, {fake} fake)',
  statsLines: '{count, plural, one {# line} other {# lines}}',
  statsKeys: '{count, plural, one {# keyframe} other {# keyframes}}',
  statsLength: '{length} long',
  statsNeverExported: 'Not exported yet',
  statsChanged: 'Changed since the last export ({name}, {when})',
  statsUnchanged: 'No changes since the last export ({name}, {when})',
  timeJustNow: 'just now',
  timeMinutes: '{n, plural, one {# minute} other {# minutes}} ago',
  timeHours: '{n, plural, one {# hour} other {# hours}} ago',
  timeDays: '{n, plural, one {# day} other {# days}} ago',

  // ---- package form ----
  packageTitle: 'Package',
  fieldName: 'Title',
  fieldLevel: 'Level label',
  fieldLevelHelp: 'Shown in song select, for example "IN Lv.15".',
  fieldDifficulty: 'Difficulty',
  fieldCharter: 'Charter',
  fieldComposer: 'Composer',
  fieldIllustrator: 'Illustrator',
  fieldPreview: 'Song-select preview',
  previewFrom: 'From',
  previewTo: 'To',
  previewHelp: 'The part of the song Phira plays in song select.',
  playPreview: 'Play the preview',
  stopPreview: 'Stop the preview',
  previewNoSong: 'The song is not loaded yet.',
  previewProblem:
    '{problem, select, negativeStart {The preview starts before the song.} endBeforeStart {The preview ends before it starts.} other {The preview ends after the song ({song} s).}} Phira refuses to open the chart.',
  fieldTags: 'Tags',
  division: 'Division',
  divisionHelp: 'Phira sorts charts into these divisions.',
  divRegular: 'Regular',
  divVisual: 'Visual',
  divTroll: 'Troll',
  divPlain: 'Plain',
  divNone: 'None',
  tagList: 'Tags of the chart',
  addTag: 'Add a tag',
  addTagPlaceholder: 'New tag, then Enter',
  removeTag: 'Remove the tag {tag}',
  fieldIntro: 'Description',
  fieldTip: 'Tip',
  tipPlaceholder: 'Shown while the chart loads (optional)',
  illustrationAlt: 'Illustration of {name}',
  noIllustration: 'No illustration',
  illustrationMissing: "The picture is missing from this browser's storage",
  illustrationFailed: 'The picture could not be shown',
  chooseIllustrationAgain: 'Choose it again...',
  chooseIllustration: 'Choose a picture...',
  changeIllustration: 'Change picture...',
  txnEditField:
    'Edit {field, select, name {the title} level {the level label} difficulty {the difficulty} charter {the charter} composer {the composer} illustrator {the illustrator} preview {the song-select preview} tags {the tags} intro {the description} other {the tip}}',
  txnEditPkg: 'Change the export {field, select, profile {profile} other {file names}}',
  txnClampPreview: 'Move the song-select preview inside the song',

  // ---- export section ----
  exportTitle: 'Export',
  exportTarget: 'Export as',
  targetPez: 'Phira / RPE package (.pez)',
  targetPezHelp:
    'Everything Phira needs. Import it in Phira as it is. The editor data is left out unless you add it (Advanced).',
  targetChart: 'Chart file only (chart.json)',
  targetChartHelp: 'The bare chart for other tools: no song, pictures or package settings.',
  targetBackup: 'Project backup',
  targetBackupHelp: 'The package plus the editor data (phiedit.json) and every file, unused ones too.',
  exportButton: 'Export...',
  copyChart: 'Copy chart.json',
  advanced: 'Advanced',
  profile: 'Profile',
  profileRpe: 'RPE-compatible',
  profileMinimal: 'Minimal (Phira only)',
  profileRpeUnverified: 'RPE-compatible (unverified)',
  profileHelp:
    'RPE-compatible pads editor-made lines and writes info.txt so RPE opens the chart the same way; Minimal writes only what Phira needs.',
  naming: 'File names',
  namingReadable: 'Readable (chart.json, music.ogg)',
  namingRpe: 'RPE id ({id}.json)',
  namingRpeNoId: 'RPE id (the chart has none)',
  namingRpeUnavailable: 'The chart has no numeric RPE id, so the files keep readable names.',
  rpeVersion: 'RPE version',
  rpeVersionProject: 'Project ({version})',
  rpeVersionHelp: 'Written as RPEVersion in exports during this session only; the project keeps {version}.',
  speedModeValue: 'Scroll speed: {mode}',
  speedClassic: 'Classic',
  speed170: 'RPE 1.7 speed curves',
  speed170Legacy: 'RPE 1.7 speed curves, older formula (RPE version {version})',
  speedHelp: 'The speed mode is changed in Project settings.',
  openProjectSettings: 'Project settings...',
  includeSidecar: 'Include editor data (phiedit.json)',
  includeSidecarHelp:
    'Layer names, colour tags, ignored problems and other editor-only data come back when the package is imported again. Phira ignores the file.',
  backupIncludes: 'A project backup always includes it.',
  includeInfoTxt: 'Include info.txt for RPE',
  includeUnused: 'Include unused files',
  pretty: 'Indented JSON',
  floatStyle: 'Numbers',
  floatRpe: 'RPE style (1.0)',
  floatShortest: 'Shortest (1)',

  // ---- export dialog ----
  dialogTitle: 'Export',
  fileName: 'File name',
  destination: 'Save to',
  destDownload: 'Downloads',
  destSaveAs: 'Choose where to save',
  destSaveAsUnavailable: 'This browser cannot ask where to save; files go to Downloads.',
  preflight: 'Preflight',
  blockingCount:
    '{count, plural, =0 {Nothing blocks the export} one {# problem blocks the export} other {# problems block the export}}',
  notesCount: '{count, plural, =0 {No conversion notes} one {# note} other {# notes}}',
  written: 'Written',
  writtenLine: 'format rpe · RPEVersion {version} · useRpe170Speed {speed} · useAttachUiFix {fix}',
  writtenChartOnly: 'format rpe · RPEVersion {version} · no info.yml (chart file only)',
  output: 'Output',
  outputFile: '{name} ({size})',
  outputFileUnknown: '{name}',
  outputTotal: 'about {size} in total',
  calculating: 'Calculating sizes...',
  calculatingNotes: 'Checking the conversion...',
  outputSummary: 'Writes {files}, about {size}.',
  outputSummaryNoSize: 'Writes {files}.',
  kindChart: 'the chart',
  kindSong: 'the song',
  kindPicture: 'the picture',
  kindOthers: '{count, plural, one {# more file} other {# more files}}',
  kindSidecar: 'the editor data',
  filesLabel: 'Files',
  outputFileEstimated: '{name} (about {size}, last export)',
  sizesAfterExport: 'The size of the chart is known after the first export.',
  exportFileLost:
    'The file "{name}" is missing from this browser\'s storage, so it cannot be put in the package. Choose the same file again in Assets, then export again.',
  exportFileLostAny:
    "A file of the chart is missing from this browser's storage, so it cannot be put in the package. Open Assets to choose it again.",
  copyNoticeSummary:
    '{count, plural, =0 {Copy chart.json: plays the same without info.yml} one {Copy chart.json: # difference in Phira without info.yml} other {Copy chart.json: # differences in Phira without info.yml}}',
  missingNotWritten: 'Missing, not written: {files}',
  switchesTitle: 'Phira will play this differently without info.yml:',
  copySwitchesTitle: 'Copy chart.json: Phira will play the copied chart differently without info.yml:',
  copyNoSwitches: 'Nothing: the copied chart plays the same without info.yml.',
  chartOnlyScope:
    'A chart file alone carries no song, files, file names or song-select preview, so the checks of those do not block it.',
  noSwitches: 'Nothing: the chart plays the same without info.yml.',
  bakeSpeed: 'Bake speed to linear for this file',
  bakeSpeedHelp:
    'Eased scroll speed becomes linear pieces, so the bare chart.json plays the same with the Phira default speed formula.',
  exportAnyway: 'Export anyway...',
  exportAnywayTitle: 'Export with problems?',
  exportAnywayBody:
    '{count, plural, one {# problem stops} other {# problems stop}} Phira from loading this chart. The file will not open in Phira until {count, plural, one {it is} other {they are}} fixed.',
  exportAnywayConfirm: 'Export anyway',
  checkingChart: 'Checking the chart...',
  waitingForCheck: 'Waiting for the check to finish...',
  checkPendingTitle: 'Export before the check has finished?',
  checkPendingBody:
    'The check of the chart is still running, so problems that stop Phira from loading it may not be listed yet.',
  export: 'Export',
  cancel: 'Cancel',
  close: 'Close',
  running: 'Exporting...',
  progressLabel: 'Export progress',
  selfCheckFailedTitle: 'The exported file does not match the chart',
  selfCheckFailedBody:
    'Reading the file back gave a different chart. This is a bug in the exporter, and the file may not play as previewed. Please report it with the details below.',
  details: 'Details',
  copyDetails: 'Copy details',
  saveAnyway: 'Save anyway...',
  saveAnywayTitle: 'Save a file that failed its check?',
  saveAnywayBody: 'The file does not match the chart and may not play in Phira as previewed.',
  saveAnywayConfirm: 'Save anyway',
  warnedTitle: 'Phira will play this file differently from the preview',
  warnedBody: 'The file matches the chart, but the export settings change how Phira plays it.',
  saveWarnedTitle: 'Save a file that plays differently in Phira?',
  saveWarnedBody: 'The export settings change how Phira plays this chart compared with the preview.',
  emptyTitle: 'The chart could not be written',
  emptyBody:
    'The exporter refused the chart (a bug in the editor). Nothing was saved. Please report it with the details below.',
  errorTitle: 'The export failed',
  unsavedWarning: 'Storage has not confirmed the latest changes yet; the export contains what you see in the editor.',
  toastExported: 'Exported {name} ({size}). In Phira: Import, then pick this file.',
  toastExportedChart: 'Saved {name} ({size}).',
  toastExportedBackup: 'Backup saved: {name} ({size}).',
  toastCopied: 'chart.json copied to the clipboard ({size}).',
  toastCopiedNoted:
    'chart.json copied to the clipboard ({size}). Without info.yml, Phira plays it differently ({count, plural, one {# setting} other {# settings}}, listed under Copy chart.json).',
  toastCopiedSwitches:
    'Without info.yml, Phira plays this chart differently ({count, plural, one {# setting} other {# settings}}). The Export dialog lists them under Copy chart.json.',
  showSwitches: 'Show',
  toastCopyFailed: 'The browser did not allow copying to the clipboard.',
  toastSaveFailed: 'The file could not be saved: {error}',
  toastDetailsCopied: 'Details copied.',
  txnRecordExport: 'Export {name}',
  savePickerDescription: '{target, select, chartJson {RPE chart} other {Phira chart package}}',
  untitled: 'Untitled',
  sizeB: '{size} B',
  sizeKB: '{size} kB',
  sizeMB: '{size} MB',
  sizeGB: '{size} GB',

  // ---- blocking problems found by the export itself ----
  blockNoMusic: 'The chart has no song.',
  blockNoMusicWhy: 'Phira cannot load a chart without a song it can decode.',
  blockMusicMissing: 'The song "{path}" is missing.',
  blockMusicUndecodable: 'Phira cannot decode the song "{path}" ({codec}).',
  blockFileMissing: 'The file "{path}" is used by the chart but missing.',
  blockFileUndecodable: 'Phira cannot decode "{path}" ({codec}).',
  blockFileWhy: 'Phira fails to load a chart when a file it needs is missing or unreadable.',
  blockGifOnImage: 'The line "{line}" has GIF progress, but its image is not a GIF.',
  blockGifOnImageWhy: 'Phira fails to load the chart: its GIF decoder cannot read the image.',
  blockPreview:
    '{problem, select, negativeStart {The song-select preview starts before the song.} endBeforeStart {The song-select preview ends before it starts ({start} s to {end} s).} other {The song-select preview ({start} s to {end} s) ends after the song ({song} s).}}',
  blockPreviewWhy: 'Phira refuses to open a chart whose song-select preview is outside the song.',
  blockSongStoredMissing: 'The song "{path}" is missing from this browser\'s storage.',
  blockFileStoredMissing: 'The file "{path}" is missing from this browser\'s storage.',
  blockStoredMissingWhy:
    'The chart still lists the file, but the browser no longer has it, so it cannot be written into the package. Choose the same file again to restore it.',
  fixAddMusic: 'Add a song...',
  fixChooseFile: 'Choose file...',
  fixChooseAgain: 'Choose the file again...',
  fixClampPreview: 'Move inside the song',
  fixEditPreview: 'Edit the preview',

  // ---- commands ----
  cmdExport: 'Export...',
  cmdCopyChart: 'Copy chart.json',
  cmdPlayPreview: 'Play the song-select preview',
  cmdClampPreview: 'Move the song-select preview inside the song',
  cmdRpeChecklist: 'Build the RPE checklist bundle (verify the RPE-compatible profile)',
  toastRpeChecklist: 'Saved {name}: open CHECKLIST.txt inside it and work through it with RPE.',
  toastRpeChecklistFailed: 'The RPE checklist bundle could not be built: {error}',
  cmdDetails: 'Edit the chart details (title, level, charter)',
  cmdFocusField: 'Go to a chart detail',
  argField: 'Detail',
  reasonNoProject: 'Open a chart first.',
  reasonPreviewOk: 'The song-select preview is already inside the song.',
  reasonNoSong: 'The length of the song is not known yet.',

  // ---- settings rows ----
  settingDestination: 'Exported files go to',
  settingDestinationHelp: 'Downloads, or a save dialog that remembers the folder (Chrome and Edge).',
  settingSidecar: 'Include editor data (phiedit.json) in packages',
  settingSidecarHelp:
    'Restores layer names, colour tags and other editor-only data when the package is imported again. Phira ignores the file.',
  settingInfoTxt: 'Write info.txt for RPE (RPE-compatible profile)',
  settingUnused: 'Include unused files in packages',
  settingPretty: 'Write indented JSON',
  settingFloat: 'Number style in exported JSON',

  // ---- help ----
  helpTitle: 'Publishing to Phira',
  helpBody:
    'Open **Publish** [[app.workspace workspace=publish]] to see whether the chart is ready: problems that stop Phira from loading it, how Phira and RPE show it, and the package details players see in song select.\n\nPress **Export** [[file.export]]. Enter in the dialog starts the export with your last settings. The editor reads every exported file back and compares it with the chart before saving it. The .pez file holds the chart, the song, the picture and info.yml.\n\nIn Phira: Import, then pick the .pez file to play it on your phone or computer.',
});

export default catalogOf(m);
